import React from 'react';
import Navbar from '../shared/Navbar';
import { Shield, Users, Lock, BookOpen } from 'lucide-react';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
            <Navbar />

            <div className="max-w-5xl mx-auto px-4 py-12">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                        Privacy Policy
                    </h1>
                    <p className="text-gray-600 text-lg">
                        STEM4KIDDO – Safe learning for children ages 5–12
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                        Effective Date: 10 January, 2026
                    </p>
                </div>

                {/* Content */}
                <div className="bg-white rounded-2xl shadow-lg p-8 space-y-10">

                    {/* Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4">
                            {/*<Shield className="w-6 h-6 text-purple-600" />*/}
                            <h2 className="text-2xl font-bold text-gray-900">1. Introduction</h2>
                        </div>
                        <p className="text-gray-700">
                            STEM4KIDDO is an educational platform designed for children aged 5–12,
                            consisting of a mobile application and a web-based portal (the “Platform”).
                        </p>
                        <ul className="list-disc ml-6 mt-4 text-gray-700 space-y-2">
                            <li>Parents or guardians create and manage child accounts</li>
                            <li>Educational institutions may manage child accounts under supervision</li>
                            <li>Children access learning content using provided credentials</li>
                        </ul>
                        <p className="text-gray-700 mt-4">
                            We comply with COPPA, Google Play Families policies, and applicable data
                            protection laws.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4">
                            {/*<Users className="w-6 h-6 text-purple-600" />*/}
                            <h2 className="text-2xl font-bold text-gray-900">2. Information We Collect</h2>
                        </div>

                        <h3 className="font-semibold mt-4">a) Account Information</h3>
                        <p className="text-gray-700">
                            Parents, guardians, or institutions may provide name, email address, country,
                            login credentials, and institutional details where applicable.
                        </p>

                        <h3 className="font-semibold mt-4">b) Child Information</h3>
                        <p className="text-gray-700">
                            Children do not create accounts or provide personal information directly.
                            Accounts are created and managed by adults.
                        </p>

                        <h3 className="font-semibold mt-4">c) Lesson Progress & Usage Data</h3>
                        <p className="text-gray-700">
                            Lesson progress and analytics are stored securely and accessible only
                            to authorized parents, guardians, or institutions.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4">
                            {/*<BookOpen className="w-6 h-6 text-purple-600" />*/}
                            <h2 className="text-2xl font-bold text-gray-900">3. Use of Information</h2>
                        </div>
                        <ul className="list-disc ml-6 text-gray-700 space-y-2">
                            <li>Provide access to STEM learning content</li>
                            <li>Track lesson completion and learning progress</li>
                            <li>Allow parents and institutions to monitor outcomes</li>
                            <li>Maintain platform security and functionality</li>
                        </ul>
                        <p className="text-gray-700 mt-4">
                            The mobile app requires authentication with credentials that can only be provided by Parents, guardians, or institutions to a child.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <div className="flex items-center gap-3 mb-4">
                            {/*<Lock className="w-6 h-6 text-purple-600" />*/}
                            <h2 className="text-2xl font-bold text-gray-900">
                                4. Data Access & Control
                            </h2>
                        </div>
                        <p className="text-gray-700">
                            Parents, guardians, and institutions have full control over child accounts,
                            including viewing progress and managing access. Children cannot modify
                            account details or view analytics.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            5. Data Access Rights
                        </h2>
                        <p className="text-gray-700">
                            Authorized adults may access, correct, or request deletion of child data
                            and withdraw consent at any time by contacting us.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            6. Data Retention & Deletion
                        </h2>
                        <p className="text-gray-700">
                            Data is retained only while accounts are active. Upon request, child data
                            is permanently deleted within a reasonable timeframe unless legally required.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            7. Data Sharing & Security
                        </h2>
                        <p className="text-gray-700">
                            STEM4KIDDO does not sell, rent, or share data with third parties. All data
                            is protected using industry-standard security measures.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            8. Ads & Third-Party Services
                        </h2>
                        <p className="text-gray-700">
                            The platform is ad-free. Analytics are limited to platform performance
                            and lesson tracking and are never visible to children.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            9. COPPA & Parental Consent
                        </h2>
                        <p className="text-gray-700">
                            Children under 13 may only use STEM4KIDDO with parental or
                            institutional consent. Parents may review or delete data at any time.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            10. Educational Institutions
                        </h2>
                        <p className="text-gray-700">
                            Institutions are responsible for obtaining parental consent and may access
                            only data for children under their supervision.
                        </p>
                    </section>

                    {/* Section */}
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            11. Changes to This Policy
                        </h2>
                        <p className="text-gray-700">
                            Updates will be communicated through the web portal or website.
                        </p>
                    </section>

                    {/* Contact */}
                    <section className="border-t pt-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-3">
                            12. Contact Us
                        </h2>
                        <p className="text-gray-700">
                            📧 privacy@fancybox.pt<br />
                        </p>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
